import {
  DISPLAY_RIGHTS_EXPIRY_NOTICE_DAYS,
  DisplayRightsProposalStatus,
  MentionBoostAuthorizationStatus,
  canCarryDisplayRightsTerms,
  displayRightsDaysUntil,
  isDisplayRightsProposalOpen,
  type DisplayRightsCompositionAction,
  type DisplayRightsProposalSummary,
} from '@fluenceur/types';

/**
 * Visual tone of a display-rights pill. Same Chakra `colorPalette` vocabulary as
 * the boost delivery badge (`DELIVERY_TONE`), so the two pills sitting side by
 * side read as one system instead of two colour languages.
 */
export type DisplayRightsTone = 'gray' | 'blue' | 'green' | 'orange';

/** Tone per proposal status. Blue is "waiting on the creator", green is agreed. */
export const DISPLAY_RIGHTS_TONE: Record<DisplayRightsProposalStatus, DisplayRightsTone> = {
  [DisplayRightsProposalStatus.DRAFT]: 'gray',
  [DisplayRightsProposalStatus.SENT]: 'blue',
  [DisplayRightsProposalStatus.ACCEPTED]: 'green',
  [DisplayRightsProposalStatus.DECLINED]: 'orange',
  [DisplayRightsProposalStatus.EXPIRED]: 'gray',
  [DisplayRightsProposalStatus.SUPERSEDED]: 'gray',
};

/**
 * The Meta side of the crossed state.
 *
 * One projection, from the authorization's REAL status. The contact card used to
 * run its own from a three-bucket summary, which is how the same authorization
 * read "revoked" on the mention panel and "ended" on the contact card.
 */
export type DisplayRightsMetaState =
  | 'pending'
  | 'authorized'
  | 'declined'
  | 'revoked'
  | 'expired'
  | 'failed';

/** Tone per Meta state, mirroring how the boost panel already colours them. */
export const DISPLAY_RIGHTS_META_TONE: Record<DisplayRightsMetaState, DisplayRightsTone> = {
  pending: 'blue',
  authorized: 'green',
  declined: 'orange',
  revoked: 'orange',
  expired: 'gray',
  failed: 'orange',
};

/** The authorization statuses map one-to-one; no state is invented here. */
export function metaStateFromAuthorizationStatus(
  status: MentionBoostAuthorizationStatus
): DisplayRightsMetaState {
  switch (status) {
    case MentionBoostAuthorizationStatus.AUTHORIZED:
      return 'authorized';
    case MentionBoostAuthorizationStatus.DECLINED:
      return 'declined';
    case MentionBoostAuthorizationStatus.REVOKED:
      return 'revoked';
    case MentionBoostAuthorizationStatus.EXPIRED:
      return 'expired';
    case MentionBoostAuthorizationStatus.FAILED:
      return 'failed';
    default:
      return 'pending';
  }
}

/**
 * Whether a sent proposal's acceptance window has already closed.
 *
 * The expiry sweep does not exist yet, so the server keeps reporting `sent`
 * until somebody opens the link. Display must not repeat that optimism: a
 * window that has passed is expired, whatever the stored status says.
 */
function isPastExpiry(summary: DisplayRightsProposalSummary, now: Date): boolean {
  if (!summary.expiresAt) {
    return false;
  }

  const expiresAt = new Date(summary.expiresAt).getTime();

  return Number.isFinite(expiresAt) && expiresAt <= now.getTime();
}

/**
 * The status a surface should SHOW, lazy expiry applied.
 *
 * Never use this to decide which endpoint to call: the server owns that through
 * the composition context's `action`. This is a display concern only.
 */
export function resolveDisplayRightsStatus(
  summary: DisplayRightsProposalSummary,
  now: Date = new Date()
): DisplayRightsProposalStatus {
  if (summary.status === DisplayRightsProposalStatus.SENT && isPastExpiry(summary, now)) {
    return DisplayRightsProposalStatus.EXPIRED;
  }

  return summary.status;
}

/**
 * Whether the creator can still act on this version.
 *
 * The single question behind both "can we remind?" and "is the link worth
 * handing over?" — the two carried identical bodies, which is exactly how they
 * would have drifted the day one of them gained a condition.
 */
export function isProposalAnswerable(
  summary: DisplayRightsProposalSummary | null,
  now: Date = new Date()
): boolean {
  if (!summary || summary.status !== DisplayRightsProposalStatus.SENT) {
    return false;
  }

  return !isPastExpiry(summary, now);
}

/**
 * A reminder only makes sense while the link is still acceptable, and only while
 * the authorization can still honour what it points at.
 *
 * The mailbox gate is separate and lives with the surface, because only the
 * surface knows whether the signed-in user has a usable mailbox.
 */
export function canRemindDisplayRights(
  summary: DisplayRightsProposalSummary | null,
  authorizationStatus?: MentionBoostAuthorizationStatus,
  now: Date = new Date()
): boolean {
  if (authorizationStatus && !canCarryDisplayRightsTerms(authorizationStatus)) {
    return false;
  }

  return isProposalAnswerable(summary, now);
}

/**
 * Whether the creator-facing link is worth handing over.
 *
 * The same two gates as the reminder, and deliberately so: a link the creator
 * can still accept, on an authorization Meta has withdrawn, buys terms that can
 * never be honoured. The three surfaces used to decide this each for
 * themselves and disagreed — the contact card gated on Meta, the mention panel
 * did not, and the composer's comment claimed the opposite of its code.
 *
 * The URL itself always comes from `summary.publicUrl` — never rebuilt from
 * `lookupKey`, which the dashboard has no base URL to prefix.
 */
export function canCopyDisplayRightsLink(
  summary: DisplayRightsProposalSummary | null,
  authorizationStatus?: MentionBoostAuthorizationStatus,
  now: Date = new Date()
): boolean {
  return canRemindDisplayRights(summary, authorizationStatus, now);
}

/**
 * The composition gesture a surface should offer.
 *
 * Two inputs, both load-bearing:
 * - the authorization first. A closed one can carry nothing, whatever the
 *   proposal says. Testing it first is the whole point: the reverse order is
 *   what let a revoked authorization keep advertising a replacement.
 * - then the RAW proposal status, not the displayed one — the raw status is what
 *   decides whether the authorization's single slot is taken, which is what the
 *   server will allow.
 *
 * Returns the server's own vocabulary, so a surface and the composition endpoint
 * cannot disagree on what the move is called. The composer still asks the server
 * for the real `action` when it opens; this decides the affordance, not the
 * endpoint.
 */
export function resolveDisplayRightsRowAction(
  summary: DisplayRightsProposalSummary | null,
  authorizationStatus?: MentionBoostAuthorizationStatus
): DisplayRightsCompositionAction {
  if (authorizationStatus && !canCarryDisplayRightsTerms(authorizationStatus)) {
    return 'none';
  }

  if (!summary) {
    return 'propose';
  }

  if (isDisplayRightsProposalOpen(summary.status)) {
    return 'revise';
  }

  if (summary.status === DisplayRightsProposalStatus.ACCEPTED) {
    // A running agreement normally leaves nothing to compose — except once its
    // term comes within reach, which is the whole point of renewing early: the
    // next term is agreed before this one runs out, so the ad never has to stop
    // while a creator considers an offer.
    return summary.isLive && !canRenewDisplayRights(summary, authorizationStatus)
      ? 'none'
      : 'renew';
  }

  return 'propose';
}

/**
 * Where the display rights stand against their own deadline.
 *
 * - `none`: no term yet. The rights have not started, so there is nothing to
 *   count down to — an agreement waiting for its first launch, or no agreement.
 * - `running`: a term exists and is comfortably ahead.
 * - `approaching`: the term is within the notice window. This is when the badge
 *   turns amber and renewing becomes the obvious next move.
 * - `ended`: the term has passed. The boost is paused and no new launch goes out
 *   until the rights are renewed.
 */
export type DisplayRightsExpiryPhase = 'none' | 'running' | 'approaching' | 'ended';

export interface DisplayRightsExpiry {
  phase: DisplayRightsExpiryPhase;
  /** ISO date the rights stop covering launches; null in the `none` phase. */
  endAt: string | null;
  /** Whole days left, negative once the term has passed; null in `none`. */
  daysLeft: number | null;
  /** True once the brand has been warned — what the notice banner renders on. */
  noticeSent: boolean;
  /** True when the deadline shown belongs to the agreement in force, not this
   *  version. That is the early-renewal state: a renewal is already out, so the
   *  panel states the deadline without offering to renew it a second time. */
  renewalPending: boolean;
}

const NO_EXPIRY: DisplayRightsExpiry = {
  phase: 'none',
  endAt: null,
  daysLeft: null,
  noticeSent: false,
  renewalPending: false,
};

/**
 * The deadline a surface should state, derived from the summary it already has
 * — no extra request anywhere.
 *
 * Reads `activeRightsEndAt`, not `rightsEndAt`: the two agree everywhere except
 * during an early renewal, where the version on screen is the successor (no term
 * of its own) while the term that is really running belongs to its predecessor.
 * Taking the version's own field there would hide the deadline at the exact
 * moment the brand has to act on it.
 */
export function resolveDisplayRightsExpiry(
  summary: DisplayRightsProposalSummary | null,
  now: Date = new Date()
): DisplayRightsExpiry {
  if (!summary?.activeRightsEndAt) return NO_EXPIRY;

  const daysLeft = displayRightsDaysUntil(summary.activeRightsEndAt, now);
  if (daysLeft === null) return NO_EXPIRY;

  const phase: DisplayRightsExpiryPhase =
    daysLeft <= 0
      ? 'ended'
      : daysLeft <= DISPLAY_RIGHTS_EXPIRY_NOTICE_DAYS
        ? 'approaching'
        : 'running';

  return {
    phase,
    endAt: summary.activeRightsEndAt,
    daysLeft,
    // The banner announces the notice, so it only appears once one was sent —
    // and only while the deadline is still ahead. Past the term the pause is the
    // news, not the warning that preceded it.
    noticeSent: phase === 'approaching' && summary.expiryNoticeSentAt !== null,
    renewalPending: summary.rightsEndAt !== summary.activeRightsEndAt,
  };
}

/**
 * Whether the panel should offer "renew the rights".
 *
 * Two conditions, both load-bearing. The deadline has to be within reach (or
 * past): renewing an agreement with months left would restart a term the brand
 * is already paying for. And no renewal may already be out — during an early
 * renewal the version on screen IS the successor, and offering to renew it would
 * ask the brand to negotiate the same thing twice.
 *
 * The server still owns the real decision through the composition context's
 * `action`; this decides the affordance, not the endpoint.
 */
export function canRenewDisplayRights(
  summary: DisplayRightsProposalSummary | null,
  authorizationStatus?: MentionBoostAuthorizationStatus,
  now: Date = new Date()
): boolean {
  if (authorizationStatus && !canCarryDisplayRightsTerms(authorizationStatus)) {
    return false;
  }

  const expiry = resolveDisplayRightsExpiry(summary, now);

  return (
    (expiry.phase === 'approaching' || expiry.phase === 'ended') &&
    !expiry.renewalPending &&
    !isDisplayRightsProposalOpen(summary!.status)
  );
}

/**
 * The label of the gesture. Split from the gesture itself because a revision
 * reads very differently once the acceptance window has passed: there is no live
 * offer left to invalidate, so "replace" would name a destruction that already
 * happened. The endpoint is unchanged — only the words are.
 *
 * `none` is excluded by the type: a surface with nothing to offer renders no
 * button, so asking for its label was unreachable by construction.
 */
export function displayRightsActionLabelKey(
  action: Exclude<DisplayRightsCompositionAction, 'none'>,
  summary: DisplayRightsProposalSummary | null,
  now: Date = new Date()
): string {
  if (
    action === 'revise' &&
    summary &&
    resolveDisplayRightsStatus(summary, now) === DisplayRightsProposalStatus.EXPIRED
  ) {
    return 'displayRights.actions.replaceExpired';
  }

  return `displayRights.actions.${action}`;
}
